import { redactTelemetryUrl } from "../redactTelemetryUrl"

/**
 * Guards PostHog event properties against the `RangeError: Maximum call stack
 * size exceeded` crash in posthog-js.
 *
 * Every `posthog.capture()` deep-copies the full event payload via
 * `_copyAndTruncateStrings -> deepCircularCopy` (posthog-core.ts). That copy
 * recurses once per level of object nesting and its ONLY safety net is a
 * reference-identity `Set`, which catches true cyclic references but NOT:
 *   1. objects nested deeper than the JS call-stack limit, or
 *   2. "pseudo-cyclic" objects whose getters return a *fresh* wrapper object on
 *      each access (Proxies, framework/SDK objects, some Error graphs) — the Set
 *      never matches the same reference, so the copy recurses without bound.
 * When such a value reaches posthog as a property the copy overflows the stack,
 * the error escapes unhandled, and Datadog RUM reports it (DD monitor 18827257).
 *
 * This function returns a value posthog can safely deep-copy: it is bounded to
 * `MAX_DEPTH` levels, replaces cycles / over-deep branches / non-plain values
 * with short placeholder strings, and — crucially — is itself depth-bounded so
 * it can NEVER overflow no matter how pathological the input. Normal analytics
 * payloads (scalars, shallow plain objects/arrays) pass through unchanged.
 */

const MAX_DEPTH = 8
const MAX_ARRAY_LENGTH = 1000
const MAX_KEYS = 1000

type Json = string | number | boolean | null | Json[] | { [key: string]: Json }

function isPlainObject(value: object): boolean {
  const proto: unknown = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function sanitizeValue(
  value: unknown,
  depth: number,
  seen: WeakSet<object>,
): Json {
  // Primitives that PostHog serializes natively.
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === "string") {
    return redactTelemetryUrl(value)
  }
  const valueType = typeof value
  if (
    valueType === "string" ||
    valueType === "number" ||
    valueType === "boolean"
  ) {
    return value as string | number | boolean
  }
  if (valueType === "bigint") {
    return (value as bigint).toString()
  }
  // Functions / symbols are not analytics data — drop to a placeholder.
  if (valueType === "function" || valueType === "symbol") {
    return `[${valueType}]`
  }

  // From here `value` is an object. The depth cap is the real overflow guard —
  // it bounds this function's own recursion regardless of the input shape.
  if (depth >= MAX_DEPTH) {
    return "[Depth limit]"
  }

  const obj = value as object

  // True cyclic reference (same object seen on the current copy path).
  if (seen.has(obj)) {
    return "[Circular]"
  }

  if (value instanceof Date) {
    return value.toISOString()
  }
  if (value instanceof Error) {
    return { name: value.name, message: redactTelemetryUrl(value.message) }
  }

  seen.add(obj)
  try {
    if (Array.isArray(value)) {
      const out: Json[] = []
      const length = Math.min(value.length, MAX_ARRAY_LENGTH)
      for (let i = 0; i < length; i++) {
        out.push(sanitizeValue(value[i], depth + 1, seen))
      }
      if (value.length > MAX_ARRAY_LENGTH) {
        out.push(`[+${value.length - MAX_ARRAY_LENGTH} more]`)
      }
      return out
    }

    // Non-plain objects (class instances, DOM nodes, Proxies, SDK resources)
    // are the usual source of getter-backed pseudo-cycles. Coerce them rather
    // than walking their (potentially infinite) property graph.
    if (!isPlainObject(obj)) {
      return safeToString(value)
    }

    const out: { [key: string]: Json } = {}
    let keyCount = 0
    for (const key in value as Record<string, unknown>) {
      if (!Object.hasOwn(value, key)) {
        continue
      }
      if (keyCount >= MAX_KEYS) {
        break
      }
      keyCount++
      let child: unknown
      try {
        // A getter can throw or recurse — isolate each property access.
        child = (value as Record<string, unknown>)[key]
      } catch {
        out[key] = "[Unreadable]"
        continue
      }
      out[key] = sanitizeValue(child, depth + 1, seen)
    }
    return out
  } finally {
    seen.delete(obj)
  }
}

function safeToString(value: unknown): string {
  try {
    return redactTelemetryUrl(String(value))
  } catch {
    return "[Unserializable]"
  }
}

/**
 * Returns a depth-bounded, cycle-safe shallow image of `properties` that
 * posthog-js can deep-copy without overflowing the call stack. Returns an empty
 * object if `properties` is missing, and never throws.
 */
export function safePostHogProperties(
  properties: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  if (!properties) {
    return {}
  }
  try {
    const seen = new WeakSet<object>()
    const result: Record<string, unknown> = {}
    let keyCount = 0
    for (const key in properties) {
      if (!Object.hasOwn(properties, key)) {
        continue
      }
      if (keyCount >= MAX_KEYS) {
        break
      }
      keyCount++
      let child: unknown
      try {
        child = properties[key]
      } catch {
        result[key] = "[Unreadable]"
        continue
      }
      result[key] = sanitizeValue(child, 1, seen)
    }
    return result
  } catch {
    // Last-resort belt-and-braces: never let analytics break a capture.
    return { __sanitize_error: true }
  }
}
